import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../../services/apiService";
import Spinner, { SpinnerCustom } from "../ui/Spinner";
import Loading from "../ui/Loading";
import Toast from "../ui/Toast";
import { FolderOpen, Search, Upload } from "lucide-react";

import CustomSelect from "../ui/CustomSelect";
function formatHistoryDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatLastImport(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({ status }) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "imported") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium bg-[var(--good-soft,#e8f3ea)] text-[var(--good,#1f6b34)]">
        Imported
      </span>
    );
  }
  if (normalized === "rolled back" || normalized === "rolled_back") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]">
        Rolled back
      </span>
    );
  }
  if (normalized === "rollback") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium bg-[var(--band,#eef3fa)] text-[#3b6ea8]">
        Rollback
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium bg-[var(--crit-soft,#fbe6e6)] text-[var(--crit,#c0262b)]">
      {status || "Failed"}
    </span>
  );
}

const filterSelectClass =
  "h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]";

export default function ImportHistoryPanel({
  onImportNew,
  onOpenMappings,
  canMutate = true,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    pageSize: 20,
  });
  const [rollingBackId, setRollingBackId] = useState(null);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const [toast, setToast] = useState({ message: null, type: "success" });
  const [detailRow, setDetailRow] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadHistory = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const result = await apiService.getReplaceHistory(nextPage, 20);
      setItems(result?.data?.items || []);
      setPagination(result?.data?.pagination || { total: 0, totalPages: 1, pageSize: 20 });
      setPage(nextPage);
    } catch (err) {
      setToast({
        message: err.message || "Failed to load import history",
        type: "error",
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  const handleRollback = async () => {
    if (!confirmRollback?.id) return;
    setRollingBackId(confirmRollback.id);
    try {
      const result = await apiService.rollbackReplaceHistory(confirmRollback.id);
      setToast({
        message:
          result?.message ||
          `Rolled back ${confirmRollback.provider} / ${confirmRollback.category}.`,
        type: "success",
      });
      setConfirmRollback(null);
      await loadHistory(page);
    } catch (err) {
      setToast({
        message: err.message || "Failed to roll back the import",
        type: "error",
      });
    } finally {
      setRollingBackId(null);
    }
  };

  const handleDownloadFile = async (row) => {
    if (!row?.id || downloadingId) return;
    if (!row.can_download_file) {
      setToast({
        message: "This import file is not available for download.",
        type: "error",
      });
      return;
    }
    setDownloadingId(row.id);
    try {
      await apiService.downloadReplaceHistoryFile(row.id, row.file);
    } catch (err) {
      setToast({
        message: err.message || "Failed to download import file",
        type: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const providerOptions = useMemo(
    () => [...new Set(items.map((r) => r.provider).filter(Boolean))].sort(),
    [items]
  );
  const categoryOptions = useMemo(
    () => [...new Set(items.map((r) => r.category).filter(Boolean))].sort(),
    [items]
  );
  const statusOptions = useMemo(
    () => [...new Set(items.map((r) => r.status).filter(Boolean))],
    [items]
  );

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((row) => {
      if (providerFilter && row.provider !== providerFilter) return false;
      if (categoryFilter && row.category !== categoryFilter) return false;
      if (statusFilter && String(row.status) !== statusFilter) return false;
      if (!q) return true;
      return (
        String(row.file || "").toLowerCase().includes(q) ||
        String(row.by || "").toLowerCase().includes(q)
      );
    });
  }, [items, searchTerm, providerFilter, categoryFilter, statusFilter]);

  const lastImport = items[0]?.date;
  const importsThisMonth = useMemo(() => {
    const now = new Date();
    return items.filter((row) => {
      const d = new Date(row.date);
      return (
        !Number.isNaN(d.getTime()) &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
      );
    }).length;
  }, [items]);

  const resetFilters = () => {
    setSearchTerm("");
    setProviderFilter("");
    setCategoryFilter("");
    setStatusFilter("");
  };

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="fixed top-4 right-4 z-[110]">
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      <div className="w-full px-5 py-2 pb-14">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Client Administration
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Inventory Imports
            </h1>
          </div>
          <div className="flex flex-wrap items-end gap-2.5">
            <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Last import
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                  {formatLastImport(lastImport)}
                </div>
              </div>
              <div className="px-3 py-2">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Imports this month
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {importsThisMonth}
                </div>
              </div>
            </div>
            {onOpenMappings ? (
              <button
                type="button"
                onClick={onOpenMappings}
                className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)]"
              >
                <FolderOpen className="h-4 w-4" />
                Field mappings
              </button>
            ) : null}
            {canMutate && (
              <button
                type="button"
                onClick={onImportNew}
                className="inline-flex h-[52px] items-center gap-2 rounded-[10px] bg-[var(--accent,#a51c1c)] px-3.5 text-[13px] font-semibold text-white hover:bg-[#8c1a1a]"
              >
                <Upload className="h-4 w-4" />
                Import new inventory
              </button>
            )}
          </div>
        </div>

        <div className="mb-4 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3 flex flex-col xl:flex-row xl:items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--faint,#9a9a95)] pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="File name or user"
                className="h-[38px] w-full pl-9 pr-3 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
              />
            </div>
          </div>
          <div className="min-w-[8.5rem]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Provider
            </label>
            <CustomSelect
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className={filterSelectClass}
            >
              <option value="">All</option>
              {providerOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </CustomSelect>
          </div>
          <div className="min-w-[8.5rem]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Category
            </label>
            <CustomSelect
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className={filterSelectClass}
            >
              <option value="">All</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </CustomSelect>
          </div>
          <div className="min-w-[8.5rem]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
              Status
            </label>
            <CustomSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={filterSelectClass}
            >
              <option value="">All</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </CustomSelect>
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-[38px] items-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
          >
            Reset
          </button>
          <p className="text-[12px] text-[var(--muted,#6b6a66)] xl:ml-auto whitespace-nowrap pb-2">
            Showing {filteredItems.length} of {items.length} imports
          </p>
        </div>

        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
          <div className="relative min-h-[240px]">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                <SpinnerCustom />
              </div>
            ) : null}

            {!loading && items.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <p className="text-[var(--ink,#1c1c1a)] font-medium mb-1">No imports yet</p>
                <p className="text-[13px] text-[var(--muted,#6b6a66)] mb-4">
                  Each replace import is logged here and can be rolled back.
                </p>
                {canMutate && (
                  <button
                    type="button"
                    onClick={onImportNew}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] text-white text-[14px] font-semibold"
                  >
                    Import new inventory
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Provider</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">File</th>
                      <th className="px-4 py-3">New</th>
                      <th className="px-4 py-3">Updated</th>
                      <th className="px-4 py-3">Left out</th>
                      <th className="px-4 py-3">By</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((row) => {
                      const leftOut = row.left_out_count ?? 0;
                      return (
                        <tr key={row.id} className="border-t border-[var(--line,#e4e2dc)] hover:bg-[var(--sunken2,#fbfaf8)]">
                          <td className="px-4 py-3 whitespace-nowrap text-[var(--ink,#1c1c1a)]">
                            {formatHistoryDate(row.date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{row.provider}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{row.category}</td>
                          <td className="px-4 py-3 max-w-[220px]">
                            {row.file ? (
                              <button
                                type="button"
                                className="block w-full text-left text-[13px] font-medium text-[var(--accent,#a51c1c)] hover:underline truncate disabled:opacity-50 disabled:cursor-wait"
                                title={
                                  row.can_download_file
                                    ? `Download ${row.file}`
                                    : `${row.file} (not available for download)`
                                }
                                disabled={downloadingId === row.id}
                                onClick={() => handleDownloadFile(row)}
                              >
                                {downloadingId === row.id ? "Downloading…" : row.file}
                              </button>
                            ) : (
                              <span className="text-[var(--faint,#9a9a95)]">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 tabular-nums font-semibold text-[var(--accent,#a51c1c)]">
                            {(row.new_count ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-[var(--ink,#1c1c1a)]">
                            {(row.updated_count ?? 0).toLocaleString()}
                          </td>
                          <td
                            className={`px-4 py-3 tabular-nums font-medium ${
                              leftOut > 0
                                ? "text-[var(--crit,#c0262b)]"
                                : "text-[var(--ink,#1c1c1a)]"
                            }`}
                          >
                            {leftOut.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{row.by}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-[13px] font-medium text-[var(--accent,#a51c1c)]">
                              <button
                                type="button"
                                className="hover:underline"
                                onClick={() => setDetailRow(row)}
                              >
                                View
                              </button>
                              {canMutate && row.can_rollback ? (
                                <>
                                  <span className="text-[var(--faint,#9a9a95)]">·</span>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 hover:underline disabled:text-[var(--faint,#9a9a95)] disabled:hover:no-underline disabled:cursor-not-allowed"
                                    onClick={() => setConfirmRollback(row)}
                                  >
                                    Roll back
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!loading && items.length > 0 && filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
                          No imports match the current filters.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-[var(--line,#e4e2dc)] flex items-center justify-between text-[13px]">
              <span className="text-[var(--muted,#6b6a66)]">
                Page {page} of {pagination.totalPages} · {pagination.total} total
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => loadHistory(page - 1)}
                  className="px-3 py-1.5 border border-[var(--line-strong,#d5d3cc)] rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() => loadHistory(page + 1)}
                  className="px-3 py-1.5 border border-[var(--line-strong,#d5d3cc)] rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {detailRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="bg-white rounded-[10px] shadow-xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)] mb-3">
              Import details
            </h3>
            <dl className="space-y-2 text-[13px]">
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">Date</dt>
                <dd>{formatHistoryDate(detailRow.date)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">Provider / Category</dt>
                <dd>
                  {detailRow.provider} · {detailRow.category}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">File</dt>
                <dd className="text-right break-all">{detailRow.file}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">Before → After</dt>
                <dd>
                  {detailRow.before_count} → {detailRow.after_count}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">New / Updated / Left out</dt>
                <dd>
                  {detailRow.new_count} / {detailRow.updated_count} /{" "}
                  {detailRow.left_out_count}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">By</dt>
                <dd>{detailRow.by}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted,#6b6a66)]">Status</dt>
                <dd>
                  <StatusBadge status={detailRow.status} />
                </dd>
              </div>
            </dl>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px]"
                onClick={() => setDetailRow(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRollback && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
          onClick={() => !rollingBackId && setConfirmRollback(null)}
        >
          <div
            className="bg-white rounded-[10px] shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)] mb-2">
              Roll back this import?
            </h3>
            <p className="text-[13px] text-[var(--muted,#6b6a66)] mb-4">
              This restores the previous {confirmRollback.provider} /{" "}
              {confirmRollback.category} inventory set and removes the inventory
              loaded by this replacement. The rollback will appear as a new row
              in history.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={!!rollingBackId}
                className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px]"
                onClick={() => setConfirmRollback(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!!rollingBackId}
                className="inline-flex items-center px-4 py-2 rounded-md text-[13px] font-medium text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] disabled:opacity-50"
                onClick={handleRollback}
              >
                {rollingBackId ? (
                  <>
                    <Spinner className="w-4 h-4 text-white" />
                    Rolling back…
                  </>
                ) : (
                  "Roll back"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Loading
        show={!!rollingBackId}
        overlayMode="fixed"
        message="Rolling back import…"
        subMessage="Restoring the previous inventory set. Please wait."
      />
    </div>
  );
}
